import { useAtom } from "jotai";
import { useState } from "react";
import { useErrorHandler } from "react-error-boundary";

import { updateFormioSubmission } from "../../../shared/api/yenta-api";
import { GradientButton } from "../../../shared/components/fkl-gradient-button/fkl-gradient-button.component";
import { cplEventsTracker } from "../../../shared/eventsTracker/events-tracker";
import { CplEventAction } from "../../../shared/eventsTracker/utils";
import { cplFormSubmissionErrorHandler } from "../../../shared/sentry/error-handler-utils";
import {
  cplOfferPageAtom,
  currentCplPageAtom,
  currentCplPageIndexAtom,
} from "../../cpl-offer-page.state";
import { moveToNextPage } from "../../utils";
import { CplOfferPageModal } from "../cpl-offer-page-modal/cpl-offer-page-modal.component";

export function CplOfferPageFooter() {
  const [cplOfferPage, setCplOfferPage] = useAtom(cplOfferPageAtom);
  const [, setCurrentCplPageIndex] = useAtom(currentCplPageIndexAtom);
  const [currentCplPage] = useAtom(currentCplPageAtom);
  const handleWidgetError = useErrorHandler();
  const [openModal, setOpenModal] = useState(false);
  const [disabledClaimEntriesBtn, setDisabledClaimEntriesBtn] = useState(false);

  const CPL_EXTRA_ENTRIES_HASH = "cplExtraEntriesHash";
  const { displayEntriesPopup, allowSkipPages, newGridUI } = window.FKL;

  const selectedCplsCounter = currentCplPage.cpls.reduce(
    (acc, cpl) => (cpl.isAccepted ? acc + 1 : acc),
    0
  );

  const canClaimExtraEntries =
    selectedCplsCounter >= currentCplPage.minimumCplOffersTaken;

  const sendCplResponsesToGA = () => {
    currentCplPage.cpls.forEach((cpl) => {
      if (cpl.gaImpressionSent) {
        cplEventsTracker({
          cplName: cpl.name,
          action: cpl.isAccepted
            ? CplEventAction.CLICK_YES
            : CplEventAction.CLICK_NO,
          value: cpl.bid,
          email: cplOfferPage.emailReceiver,
        });
      }
    });
  };

  const moveToNextCplOfferPage = (isNoThanksAction = false) => {
    sessionStorage.removeItem(CPL_EXTRA_ENTRIES_HASH);
    setCurrentCplPageIndex((p) => {
      const nextPage = p + 1;
      if (cplOfferPage.pages[nextPage]) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        setDisabledClaimEntriesBtn(false);
        return nextPage;
      }

      if (!isNoThanksAction && displayEntriesPopup) {
        setOpenModal(true);
      } else {
        moveToNextPage();
      }

      return p;
    });
  };

  const persistAcceptedCplsAndExtraEntries = async () => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    if (!submission) {
      console.error("No submission to update.");
      return;
    }

    try {
      const sweepFormUrl =
        document
          .querySelector("#FormSweepstake")
          ?.getAttribute("data-form-path-url") ?? "";
      const updatedSubmission = await window.theme.RecaptchaUtils.updateSubmissionToken(
        submission
      );
      window.theme.Sweepstake.prototype.storeSubmission(updatedSubmission);
      await updateFormioSubmission(
        sweepFormUrl,
        {
          ...updatedSubmission,
          data: {
            ...updatedSubmission.data,
            cplExtraEntries:
              (cplOfferPage.initialCplExtraEntries || 0) +
              cplOfferPage.cplExtraEntries,
            quizAcceptedCpls: [
              ...new Set(
                (cplOfferPage.initialAcceptedCpls ?? "")
                  .split(",")
                  .concat(
                    currentCplPage.cpls
                      .filter((cpl) => cpl.isAccepted && cpl.name !== "")
                      .map((cpl) => cpl.name)
                  )
                  .filter((cplName) => cplName !== "")
              ),
            ].join(","),
          },
        },
        handleWidgetError
      ).then((submission: any) => {
        setCplOfferPage({
          ...cplOfferPage,
          sessionCplExtraEntries:
            cplOfferPage.sessionCplExtraEntries! +
            ((submission.data?.cplExtraEntries ?? 0) -
              cplOfferPage.initialCplExtraEntries!),
          initialCplExtraEntries: submission.data?.cplExtraEntries ?? 0,
          initialAcceptedCpls: submission.data?.quizAcceptedCpls ?? "",
        });
        window.theme.Sweepstake.prototype.storeSubmission(submission);
        window.theme.Sweepstake.prototype.storeSubmissionHash(submission);
      });

      await Promise.all(
        currentCplPage.cpls
          .filter((cpl) => cpl.isAccepted && typeof cpl.formPath === "string")
          .map((cpl) => cpl.formPath)
          .map((formPath) => {
            const baseURL = window.theme.QuizUtils.getBaseURLForCPLForm(
              formPath
            );
            const formio = new window.Formio(`${baseURL}/${formPath}`);

            return formio.saveSubmission({
              data: {
                email: cplOfferPage.emailReceiver,
                agreeToTerms: true,
              },
            });
          })
      ).catch((err) => {
        cplFormSubmissionErrorHandler(err);
      });
    } catch (error) {
      console.error(error);
    } finally {
      moveToNextCplOfferPage();
    }
  };

  const handleClaimEntriesBtnClick = async () => {
    setDisabledClaimEntriesBtn(true);
    sendCplResponsesToGA();
    await persistAcceptedCplsAndExtraEntries();
  };

  const handleNoThanksBtnClick = () => {
    sendCplResponsesToGA();
    moveToNextCplOfferPage(true);
  };

  if (newGridUI) {
    return (
      <>
        <div className="cpl-offer-page__footer">
          <div className="cpl-offer-page__footer--actions">
            {allowSkipPages && (
              <button
                className="cpl-offer-page__footer--action-no-thanks"
                onClick={() => handleNoThanksBtnClick()}
              >
                No thanks, I’ll skip my extra entries
              </button>
            )}
            <div className="cpl-offer-page__footer--action-claim-offers">
              <div className="cpl-offer-page__footer--action-claim-offers--instruction">
                {`Pick ${currentCplPage.minimumCplOffersTaken} or more to
                  continue`}
              </div>
              <GradientButton
                disabled={!canClaimExtraEntries || disabledClaimEntriesBtn}
                onClick={() => handleClaimEntriesBtnClick()}
              >
                Earn More Entries
              </GradientButton>
            </div>
          </div>
          <div className="cpl-offer-page__footer--copy">
            Your offers will be sent to{" "}
            <strong>{cplOfferPage.emailReceiver}</strong>. By accepting the
            offers, you will be opting into email marketing from brands selected
            above.
          </div>
        </div>
        <CplOfferPageModal isOpen={openModal} />
      </>
    );
  }

  return (
    <>
      <div className="cpl-offer-page__footer">
        <div className="cpl-offer-page__footer--actions">
          {allowSkipPages && (
            <button
              className="cpl-offer-page__footer--action-no-thanks"
              onClick={() => handleNoThanksBtnClick()}
            >
              No thanks, I’ll skip my extra entries
            </button>
          )}
          <div className="cpl-offer-page__footer--action-claim-offers">
            <button
              className="cpl-offer-page--btn"
              disabled={!canClaimExtraEntries || disabledClaimEntriesBtn}
              onClick={() => handleClaimEntriesBtnClick()}
            >
              Earn More Entries
            </button>
            <div className="cpl-offer-page__footer--action-claim-offers--feedback">
              {!canClaimExtraEntries &&
                `Select at least ${currentCplPage.minimumCplOffersTaken} ${
                  currentCplPage.minimumCplOffersTaken > 1 ? "offers" : "offer"
                } to claim`}
            </div>
          </div>
        </div>
        <div className="cpl-offer-page__footer--copy">
          Your offers will be sent to{" "}
          <strong>{cplOfferPage.emailReceiver}</strong>. By accepting the
          offers, you will be opting into email marketing from brands selected
          above.
        </div>
      </div>
      <CplOfferPageModal isOpen={openModal} />
    </>
  );
}
